/**
 * Floor-plan content for print viewports: the same entities the DXF export
 * writes (layout + objects with their labels), built in meters, with label
 * offsets sized for the text the sheet prints.
 */

import type { DxfBlock, DxfEntity } from '@pointorama/dxf-exporter';
import { planEntityBounds, type PlanBounds } from '@pointorama/print-engine';
import { useCallback } from 'react';
import { paperMmToWorld, planTextSize, textSizeToCapMm, type ViewportSettings } from 'shared-types';
import { floorExportMode } from '../../../hooks/potree/floorExportMode';
import { DxfExportTypes, useExportDXF, useExportLayoutDXF } from '../../../hooks/potree/useExportDXF';
import { useFlatAnnotationList } from '../../../hooks/potree/useFlatAnnotationList';
import { useResolveFloorGroup } from '../../../hooks/potree/useResolveFloorGroup';

export interface PlanEntities {
  entities: DxfEntity[];
  blocks: DxfBlock[];
  bounds: PlanBounds | null;
}

export type BuildPlanEntities = (floorId: string, settings: Pick<ViewportSettings, 'scaleDen' | 'textSizes'>, capHeightRatio: number) => PlanEntities;

export function usePlanContent(): BuildPlanEntities {
  const { buildLayoutDXF } = useExportLayoutDXF();
  const [, , buildObjectsDXF] = useExportDXF();
  const { allAnnotations } = useFlatAnnotationList();
  const resolveFloorGroup = useResolveFloorGroup();

  return useCallback(
    (floorId, settings, capHeightRatio) => {
      // World height of the printed capitals: labels are placed around text of that size.
      const worldCap = (category: 'labels' | 'dimensions' | 'roomTags') => paperMmToWorld(textSizeToCapMm(planTextSize(settings, category), capHeightRatio), settings.scaleDen);
      const textHeightMeters = { labels: worldCap('labels'), dimensions: worldCap('dimensions'), roomTags: worldCap('roomTags') };
      const children = allAnnotations.filter((a) => resolveFloorGroup(a.groupIdentifier) === floorId);
      const layout = buildLayoutDXF([floorId], { metric: true, textHeightMeters, separateDimensions: true, multilineTags: true });
      // As-built floors also print their measured objects (rooms, structures, …) with their labels.
      const objects =
        floorExportMode(floorId, children) === 'OBJECTS'
          ? buildObjectsDXF(
              children.filter((a) => a.type in DxfExportTypes),
              { includeLabels: true, metric: true, textHeightMeters },
            )
          : [];
      const entities = [...layout.entities, ...objects];
      return { entities, blocks: layout.blocks, bounds: planEntityBounds(entities) };
    },
    [allAnnotations, resolveFloorGroup, buildLayoutDXF, buildObjectsDXF],
  );
}
